import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Plus, Trash2, Edit2, Save, X, DollarSign, 
  Calendar, Check, Clock, RefreshCw, Calculator, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'casual', label: 'Casual' },
  { value: 'contractor', label: 'Contractor' }
];

function PayrollTab() {
  const [employees, setEmployees] = useState([]);
  const [payRuns, setPayRuns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('employees');
  
  // Employee form
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({
    name: '', email: '', position: '', employment_type: 'full_time',
    pay_rate: '', pay_type: 'salary', super_rate: 11.5, start_date: ''
  });
  
  // Pay run form
  const [showPayRunForm, setShowPayRunForm] = useState(false);
  const [calculatedPay, setCalculatedPay] = useState(null);
  const [payRunForm, setPayRunForm] = useState({
    employee_id: '', pay_period_start: '', pay_period_end: '', hours_worked: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, payRes, sumRes] = await Promise.all([
        axios.get(`${API}/payroll/employees`, { withCredentials: true }),
        axios.get(`${API}/payroll/pay-runs?limit=50`, { withCredentials: true }),
        axios.get(`${API}/payroll/summary`, { withCredentials: true })
      ]);
      setEmployees(empRes.data.employees || []);
      setPayRuns(payRes.data.pay_runs || []);
      setSummary(sumRes.data);
    } catch (error) {
      console.error('Failed to fetch payroll data:', error);
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveEmployee = async (e) => {
    e.preventDefault();
    if (!employeeForm.name || !employeeForm.pay_rate) {
      toast.error('Name and pay rate are required');
      return;
    }
    
    try {
      const data = { ...employeeForm, pay_rate: parseFloat(employeeForm.pay_rate) };
      if (editingEmployee) {
        await axios.put(`${API}/payroll/employees/${editingEmployee}`, data, { withCredentials: true });
        toast.success('Employee updated');
      } else {
        await axios.post(`${API}/payroll/employees`, data, { withCredentials: true });
        toast.success('Employee added');
      }
      setShowEmployeeForm(false);
      setEditingEmployee(null);
      resetEmployeeForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save employee');
    }
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm('Deactivate this employee?')) return;
    try {
      await axios.delete(`${API}/payroll/employees/${id}`, { withCredentials: true });
      toast.success('Employee deactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to deactivate employee');
    }
  };

  const handleEditEmployee = (emp) => {
    setEmployeeForm({
      name: emp.name, email: emp.email || '', position: emp.position || '',
      employment_type: emp.employment_type, pay_rate: emp.pay_rate,
      pay_type: emp.pay_type, super_rate: emp.super_rate || 11.5, start_date: emp.start_date || ''
    });
    setEditingEmployee(emp.employee_id);
    setShowEmployeeForm(true);
  };

  const resetEmployeeForm = () => {
    setEmployeeForm({
      name: '', email: '', position: '', employment_type: 'full_time',
      pay_rate: '', pay_type: 'salary', super_rate: 11.5, start_date: ''
    });
  };

  const handleCalculatePay = async () => {
    if (!payRunForm.employee_id || !payRunForm.pay_period_start || !payRunForm.pay_period_end) {
      toast.error('Please select employee and pay period');
      return;
    }
    
    try {
      const params = new URLSearchParams({
        employee_id: payRunForm.employee_id,
        pay_period_start: payRunForm.pay_period_start,
        pay_period_end: payRunForm.pay_period_end
      });
      if (payRunForm.hours_worked) params.append('hours_worked', payRunForm.hours_worked);
      
      const response = await axios.post(`${API}/payroll/calculate?${params}`, {}, { withCredentials: true });
      setCalculatedPay(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate pay');
    }
  };

  const handleCreatePayRun = async () => {
    if (!calculatedPay) {
      toast.error('Please calculate pay first');
      return;
    }
    
    try {
      await axios.post(`${API}/payroll/pay-runs`, {
        employee_id: calculatedPay.employee_id,
        pay_period_start: calculatedPay.pay_period_start,
        pay_period_end: calculatedPay.pay_period_end,
        hours_worked: calculatedPay.hours_worked,
        gross_pay: calculatedPay.gross_pay,
        tax_withheld: calculatedPay.tax_withheld,
        super_amount: calculatedPay.super_amount,
        net_pay: calculatedPay.net_pay
      }, { withCredentials: true });
      
      toast.success('Pay run created');
      setShowPayRunForm(false);
      setCalculatedPay(null);
      setPayRunForm({ employee_id: '', pay_period_start: '', pay_period_end: '', hours_worked: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create pay run');
    }
  };

  const handleApprovePayRun = async (id) => {
    try {
      await axios.post(`${API}/payroll/pay-runs/${id}/approve`, {}, { withCredentials: true });
      toast.success('Pay run approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve pay run');
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await axios.post(`${API}/payroll/pay-runs/${id}/mark-paid`, {}, { withCredentials: true });
      toast.success('Pay run marked as paid');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark as paid');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payroll-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="text-sm text-slate-500 dark:text-slate-400">Employees</div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">{summary?.employees?.total || 0}</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="text-sm text-slate-500 dark:text-slate-400">Total Gross</div>
          <div className="text-2xl font-bold text-emerald-600">${(summary?.totals?.gross_pay || 0).toLocaleString()}</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="text-sm text-slate-500 dark:text-slate-400">Tax Withheld</div>
          <div className="text-2xl font-bold text-amber-600">${(summary?.totals?.tax_withheld || 0).toLocaleString()}</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="text-sm text-slate-500 dark:text-slate-400">Super</div>
          <div className="text-2xl font-bold text-indigo-600">${(summary?.totals?.superannuation || 0).toLocaleString()}</div>
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-700 pb-2">
        {[
          { id: 'employees', label: 'Employees', icon: Users },
          { id: 'payruns', label: 'Pay Runs', icon: DollarSign },
          { id: 'reports', label: 'Reports', icon: FileText }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveView(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeView === tab.id
                ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
            data-testid={`payroll-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Employees View */}
      {activeView === 'employees' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Employees</h3>
            <button
              onClick={() => { resetEmployeeForm(); setEditingEmployee(null); setShowEmployeeForm(true); }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2"
              data-testid="add-employee-btn"
            >
              <Plus className="w-4 h-4" />
              Add Employee
            </button>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
            {employees.length === 0 ? (
              <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No employees yet. Add your first employee to get started.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-700/50">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Name</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Position</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Type</th>
                      <th className="text-right px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Pay Rate</th>
                      <th className="text-center px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {employees.map(emp => (
                      <tr key={emp.employee_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900 dark:text-white">{emp.name}</div>
                          {emp.email && <div className="text-xs text-slate-500">{emp.email}</div>}
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{emp.position || '-'}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-xs capitalize">
                            {emp.employment_type.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-slate-900 dark:text-white">
                          ${emp.pay_rate.toLocaleString()}/{emp.pay_type === 'hourly' ? 'hr' : 'yr'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center gap-1">
                            <button
                              onClick={() => handleEditEmployee(emp)}
                              className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteEmployee(emp.employee_id)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pay Runs View */}
      {activeView === 'payruns' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Pay Runs</h3>
            <button
              onClick={() => setShowPayRunForm(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2"
              data-testid="create-payrun-btn"
            >
              <Plus className="w-4 h-4" />
              Create Pay Run
            </button>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
            {payRuns.length === 0 ? (
              <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No pay runs yet. Create your first pay run.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-700/50">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Employee</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Period</th>
                      <th className="text-right px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Gross</th>
                      <th className="text-right px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Net</th>
                      <th className="text-center px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Status</th>
                      <th className="text-center px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {payRuns.map(pr => (
                      <tr key={pr.pay_run_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                        <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{pr.employee_name}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs">
                          {pr.pay_period_start} to {pr.pay_period_end}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-900 dark:text-white">${pr.gross_pay.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-medium text-emerald-600">${pr.net_pay.toLocaleString()}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            pr.status === 'paid' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' :
                            pr.status === 'approved' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' :
                            'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                          }`}>
                            {pr.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center gap-1">
                            {pr.status === 'draft' && (
                              <button
                                onClick={() => handleApprovePayRun(pr.pay_run_id)}
                                className="px-2 py-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded"
                              >
                                Approve
                              </button>
                            )}
                            {pr.status === 'approved' && (
                              <button
                                onClick={() => handleMarkPaid(pr.pay_run_id)}
                                className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded"
                              >
                                Mark Paid
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reports View */}
      {activeView === 'reports' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Payroll Reports</h3>
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            View summaries and prepare for STP lodgment.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
              <h4 className="font-medium text-slate-900 dark:text-white">YTD Summary</h4>
              <p className="text-sm text-slate-500 mt-1">Gross: ${(summary?.totals?.gross_pay || 0).toLocaleString()}</p>
              <p className="text-sm text-slate-500">Tax: ${(summary?.totals?.tax_withheld || 0).toLocaleString()}</p>
              <p className="text-sm text-slate-500">Super: ${(summary?.totals?.superannuation || 0).toLocaleString()}</p>
            </div>
            <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
              <h4 className="font-medium text-slate-900 dark:text-white">By Type</h4>
              <p className="text-sm text-slate-500 mt-1">Full Time: {summary?.employees?.full_time || 0}</p>
              <p className="text-sm text-slate-500">Part Time: {summary?.employees?.part_time || 0}</p>
              <p className="text-sm text-slate-500">Casual: {summary?.employees?.casual || 0}</p>
            </div>
            <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-amber-50 dark:bg-amber-900/20">
              <h4 className="font-medium text-amber-800 dark:text-amber-300">STP Note</h4>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                For actual STP lodgment, use ATO-certified payroll software.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Employee Form Modal */}
      <AnimatePresence>
        {showEmployeeForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEmployeeForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white dark:bg-slate-800 rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                {editingEmployee ? 'Edit Employee' : 'Add Employee'}
              </h3>
              
              <form onSubmit={handleSaveEmployee} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Name *</label>
                    <input
                      type="text"
                      value={employeeForm.name}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                      data-testid="employee-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email</label>
                    <input
                      type="email"
                      value={employeeForm.email}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Position</label>
                    <input
                      type="text"
                      value={employeeForm.position}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, position: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Employment Type</label>
                    <select
                      value={employeeForm.employment_type}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, employment_type: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    >
                      {EMPLOYMENT_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Pay Type</label>
                    <select
                      value={employeeForm.pay_type}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, pay_type: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    >
                      <option value="salary">Salary (Annual)</option>
                      <option value="hourly">Hourly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Pay Rate * ({employeeForm.pay_type === 'hourly' ? '/hr' : '/yr'})
                    </label>
                    <input
                      type="number"
                      value={employeeForm.pay_rate}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, pay_rate: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                      data-testid="employee-payrate-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Super Rate %</label>
                    <input
                      type="number"
                      step="0.5"
                      value={employeeForm.super_rate}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, super_rate: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <button type="button" onClick={() => setShowEmployeeForm(false)} className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
                    Cancel
                  </button>
                  <button type="submit" className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center gap-2" data-testid="save-employee-btn">
                    <Save className="w-4 h-4" />
                    Save
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pay Run Form Modal */}
      <AnimatePresence>
        {showPayRunForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => { setShowPayRunForm(false); setCalculatedPay(null); }}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white dark:bg-slate-800 rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Create Pay Run</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Employee</label>
                  <select
                    value={payRunForm.employee_id}
                    onChange={(e) => setPayRunForm({ ...payRunForm, employee_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    data-testid="payrun-employee-select"
                  >
                    <option value="">Select employee...</option>
                    {employees.map(emp => (
                      <option key={emp.employee_id} value={emp.employee_id}>{emp.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Period Start</label>
                    <input
                      type="date"
                      value={payRunForm.pay_period_start}
                      onChange={(e) => setPayRunForm({ ...payRunForm, pay_period_start: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Period End</label>
                    <input
                      type="date"
                      value={payRunForm.pay_period_end}
                      onChange={(e) => setPayRunForm({ ...payRunForm, pay_period_end: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Hours Worked (for hourly)</label>
                  <input
                    type="number"
                    value={payRunForm.hours_worked}
                    onChange={(e) => setPayRunForm({ ...payRunForm, hours_worked: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    placeholder="Leave blank for salaried employees"
                  />
                </div>
                
                <button
                  type="button"
                  onClick={handleCalculatePay}
                  className="w-full px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center justify-center gap-2"
                  data-testid="calculate-pay-btn"
                >
                  <Calculator className="w-4 h-4" />
                  Calculate Pay
                </button>
                
                {calculatedPay && (
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                    <h4 className="font-medium text-emerald-800 dark:text-emerald-300 mb-2">Pay Calculation</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span>Gross Pay:</span><span className="font-medium">${calculatedPay.gross_pay}</span></div>
                      <div className="flex justify-between"><span>Tax Withheld:</span><span className="font-medium text-red-600">-${calculatedPay.tax_withheld}</span></div>
                      <div className="flex justify-between"><span>Super ({calculatedPay.super_rate}%):</span><span className="font-medium">${calculatedPay.super_amount}</span></div>
                      <div className="flex justify-between border-t border-emerald-200 dark:border-emerald-700 pt-1 mt-1">
                        <span className="font-medium">Net Pay:</span>
                        <span className="font-bold text-emerald-700 dark:text-emerald-300">${calculatedPay.net_pay}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex justify-end gap-3 pt-4">
                  <button type="button" onClick={() => { setShowPayRunForm(false); setCalculatedPay(null); }} className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreatePayRun}
                    disabled={!calculatedPay}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    data-testid="create-payrun-submit-btn"
                  >
                    <Check className="w-4 h-4" />
                    Create Pay Run
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default PayrollTab;
